import { formatGender, formatNameLastFirst } from '@strata/helper-functions';
import { Dialog } from '@strata/react-dialog';
import { InlineMessage } from '@strata/react-inline-message';
import { AddressDetailLabelValue, DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { ComponentProps } from 'react';

import { BenefitCheckResponse } from 'store/api/scopeAssessorApi';

const Statuses = {
    ELIGIBLE: 'eligible',
    INELIGIBLE: 'ineligible',
    PATIENT_NOT_FOUND: 'patient-not-found',
} as const;

const getDialogStatus = (benefitInfo?: BenefitCheckResponse) => {
    if (!benefitInfo) {
        return Statuses.PATIENT_NOT_FOUND;
    }
    return benefitInfo.is_eligible ? Statuses.ELIGIBLE : Statuses.INELIGIBLE;
};

const DialogTitle = {
    [Statuses.ELIGIBLE]: 'Confirm Patient',
    [Statuses.INELIGIBLE]: 'Ineligible Patient',
    [Statuses.PATIENT_NOT_FOUND]: 'Patient Not Found',
} as const;

const DialogInlineMessage = {
    [Statuses.ELIGIBLE]: (
        <InlineMessage intent={InlineMessage.Intent.SUCCESS} label="Patient is eligible." />
    ),
    [Statuses.INELIGIBLE]: (
        <InlineMessage intent={InlineMessage.Intent.ERROR} label="Patient is not eligible." />
    ),
    [Statuses.PATIENT_NOT_FOUND]: (
        <InlineMessage
            intent={InlineMessage.Intent.ERROR}
            label="Unable to verify benefits and eligibility."
        />
    ),
} as const;

interface EligibilityDialogProps
    extends Pick<ComponentProps<typeof Dialog>, 'onPrimaryButtonClick' | 'onSecondaryButtonClick'> {
    benefitInfo?: BenefitCheckResponse;
    isOpen?: boolean;
}

export const EligibilityDialog = ({
    benefitInfo = undefined,
    isOpen = false,
    onPrimaryButtonClick,
    onSecondaryButtonClick,
}: EligibilityDialogProps) => {
    const dialogStatus = getDialogStatus(benefitInfo);
    return (
        <Dialog
            dialogTitle={DialogTitle[dialogStatus]}
            primaryButtonLabel="Confirm Patient"
            isPrimaryButtonDisabled={dialogStatus !== Statuses.ELIGIBLE}
            secondaryButtonLabel="Return to Form"
            isOpen={isOpen}
            onPrimaryButtonClick={onPrimaryButtonClick}
            onSecondaryButtonClick={onSecondaryButtonClick}
            dialogContent={
                <>
                    <div className="margin__bottom--large">{DialogInlineMessage[dialogStatus]}</div>
                    {dialogStatus === Statuses.PATIENT_NOT_FOUND ? (
                        <div>
                            If you have entered incorrect information, please return to the form and
                            correct it.
                        </div>
                    ) : (
                        <>
                            <div className="flex__gap--xx-large margin__bottom--large">
                                <DetailLabelValuePair
                                    label="Patient Name"
                                    value={formatNameLastFirst(
                                        benefitInfo?.patient_last_name,
                                        benefitInfo?.patient_first_name,
                                    )}
                                />
                                <DetailLabelValuePair label="Age" value={benefitInfo?.member_age} />
                                <DetailLabelValuePair
                                    label="Gender"
                                    value={formatGender(benefitInfo?.gender)}
                                />
                            </div>
                            <AddressDetailLabelValue
                                address1={benefitInfo?.patient_address_line_1}
                                address2={benefitInfo?.patient_address_line_2}
                                city={benefitInfo?.member_city}
                                state={benefitInfo?.member_state}
                                zip={benefitInfo?.member_zip}
                            />
                        </>
                    )}
                </>
            }
        />
    );
};
